import { useCallback, useEffect, useRef, useState } from 'react';
import { BuildsError } from './buildsApi';

/**
 * Bounded live updates (#1624, R10, W23): polling only, never two requests in flight, an interval the data decides
 * (null: stop), ±10 % jitter, 30 s while the tab is hidden, and on errors a backoff ×2 up to 60 s ("Reconnecting…").
 * A 404 stops polling: the build is gone, or no longer the viewer's to see.
 */

export const POLL_MS = { running: 3000, queued: 5000, list: 5000, hidden: 30000, backoffMax: 60000 } as const;

export function nextDelay(base: number, { hidden, failures, random = Math.random }: { hidden: boolean; failures: number; random?: () => number }): number {
  const every = hidden ? Math.max(base, POLL_MS.hidden) : base;
  const ms = failures > 0 ? Math.min(every * 2 ** failures, POLL_MS.backoffMax) : every;
  return Math.round(ms * (0.9 + 0.2 * random()));
}

export interface Polled<T> {
  data: T | null;
  error: BuildsError | null;
  reconnecting: boolean;
  /** Show this now (an action's reply) and poll again from here. */
  replace: (data: T) => void;
}

/**
 * What one read fetched: the data, and `commit`, which advances the reader's validator (and anything else that names
 * what is on screen) to it. `commit` runs only once the data is shown, so a validator never names a body the page does
 * not hold: a read whose result is dropped (a failed follow-up fetch, an action's reply shown instead, a restart, an
 * unmount) leaves the validator where it was, and the next poll fetches that body again.
 */
export interface Fetched<T> { data: T; commit: () => void }

/** `read(live)` returns what it fetched, or `undefined` when nothing shown changed (a 304); `live()` turns false once its
 * result would be dropped (an unmount, a restart), and a read sends nothing more after that. */
export function usePolled<T>(key: string | null, read: (live: () => boolean) => Promise<Fetched<T> | undefined>, every: (data: T) => number | null): Polled<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<BuildsError | null>(null);
  const [failures, setFailures] = useState(0);
  const [restart, setRestart] = useState(0);
  const readRef = useRef(read);
  const everyRef = useRef(every);
  readRef.current = read;
  everyRef.current = every;
  const inFlight = useRef<Promise<unknown> | null>(null);       // across restarts: one request at a time, ever
  const latest = useRef<T | null>(null);

  useEffect(() => {
    latest.current = null;
    setData(null); setError(null); setFailures(0);
  }, [key]);

  useEffect(() => {
    if (key === null) return undefined;
    let live = true, timer: ReturnType<typeof setTimeout> | undefined, waiting = false, failed = 0;
    const schedule = (ms: number) => { clearTimeout(timer); waiting = true; timer = setTimeout(run, ms); };
    async function run() {
      waiting = false;
      while (inFlight.current) {
        try { await inFlight.current; } catch { /* the earlier request's own error is its own */ }
        if (!live) return;
      }
      const request = readRef.current(() => live);
      inFlight.current = request;
      let gone = false;
      try {
        const got = await request;
        if (!live) return;
        failed = 0;
        if (got !== undefined) { latest.current = got.data; setData(got.data); got.commit(); }
        setError(null); setFailures(0);
      } catch (e: any) {
        if (!live) return;
        const err = e instanceof BuildsError ? e : new BuildsError('unreachable', 0);
        setError(err);
        if (err.status === 404) gone = true;
        else { failed += 1; setFailures(failed); }
      } finally {
        if (inFlight.current === request) inFlight.current = null;
      }
      if (!live || gone) return;
      const base = latest.current === null ? POLL_MS.running : everyRef.current(latest.current);
      if (base === null && failed === 0) return;
      schedule(nextDelay(base ?? POLL_MS.running, { hidden: document.hidden, failures: failed }));
    }
    const onVisible = () => { if (!document.hidden && waiting) schedule(0); };
    document.addEventListener('visibilitychange', onVisible);
    void run();
    return () => { live = false; clearTimeout(timer); document.removeEventListener('visibilitychange', onVisible); };
  }, [key, restart]);

  const replace = useCallback((next: T) => {
    latest.current = next;
    setData(next); setError(null); setFailures(0);
    setRestart((r) => r + 1);
  }, []);
  return { data, error, reconnecting: failures > 0, replace };
}
